import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Cookie, Heart } from 'lucide-react';
import { rtlLanguages } from '../i18n/config';

const COOKIE_CONSENT_KEY = 'macsuper-cookie-consent';
const THEME_STORAGE_KEY = 'macsuper-theme';

window.checkCookieConsent = () => {
  const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
  return consent === 'accepted';
};

window.requestCookieConsentForTheme = null;
window.onCookieConsentAccepted = null;
window.resetThemeToSystem = null;

export default function Footer({ openContactPopup, openPrivacyPopup }) {
  const { t, i18n } = useTranslation();
  const isRTL = rtlLanguages.includes(i18n.language);
  const [showCookiesModal, setShowCookiesModal] = useState(false);
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const [cookieConsent, setCookieConsent] = useState(null);
  const [bannerMessage, setBannerMessage] = useState('default');

  useEffect(() => {
    // Don't show cookie banner during prerendering
    if (typeof window !== 'undefined' && window.__PRERENDERING__) {
      return;
    }

    window.requestCookieConsentForTheme = () => {
      setBannerMessage('theme');
      setShowCookieBanner(true);
    };

    const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (savedConsent === null) {
      const timer = setTimeout(() => {
        setBannerMessage('default');
        setShowCookieBanner(true);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setCookieConsent(savedConsent === 'accepted');

      // Update Google Analytics consent based on saved preference
      if (typeof window.updateGoogleAnalyticsConsent === 'function') {
        window.updateGoogleAnalyticsConsent(savedConsent === 'accepted');
      } else if (typeof window.gtag !== 'undefined') {
        // Fallback to direct gtag call
        window.gtag('consent', 'update', {
          'analytics_storage': savedConsent === 'accepted' ? 'granted' : 'denied'
        });
      }
    }
  }, []);

  const handleAcceptCookies = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
    setCookieConsent(true);
    setShowCookieBanner(false);

    const wasThemeRequest = bannerMessage === 'theme';
    setBannerMessage('default');

    // Update Google Analytics consent to enable cookies
    if (typeof window.updateGoogleAnalyticsConsent === 'function') {
      window.updateGoogleAnalyticsConsent(true);
    } else if (typeof window.gtag !== 'undefined') {
      // Fallback to direct gtag call
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }

    if (wasThemeRequest && typeof window.onCookieConsentAccepted === 'function') {
      window.onCookieConsentAccepted();
    }
  };

  const handleDeclineCookies = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'declined');
    setCookieConsent(false);
    setShowCookieBanner(false);
    setBannerMessage('default');

    localStorage.removeItem(THEME_STORAGE_KEY);

    if (typeof window.resetThemeToSystem === 'function') {
      window.resetThemeToSystem();
    }

    // Update Google Analytics consent to disable tracking
    if (typeof window.updateGoogleAnalyticsConsent === 'function') {
      window.updateGoogleAnalyticsConsent(false);
    } else if (typeof window.gtag !== 'undefined') {
      // Fallback to direct gtag call
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied'
      });
    }
  };

  const handleCloseBanner = () => {
    setShowCookieBanner(false);
    setBannerMessage('default');
  };

  const Modal = ({ isOpen, onClose, title, children }) => {
    useEffect(() => {
      if (isOpen) {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.documentElement.style.overflow = 'hidden';
        if (scrollbarWidth > 0) {
          document.documentElement.style.paddingRight = `${scrollbarWidth}px`;
          const header = document.querySelector('header');
          if (header) header.style.paddingRight = `${scrollbarWidth}px`;
        }
      } else {
        document.documentElement.style.overflow = '';
        document.documentElement.style.paddingRight = '';
        const header = document.querySelector('header');
        if (header) header.style.paddingRight = '';
      }
      return () => {
        document.documentElement.style.overflow = '';
        document.documentElement.style.paddingRight = '';
        const header = document.querySelector('header');
        if (header) header.style.paddingRight = '';
      };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
        onClick={onClose}
      >
        <div
          className="bg-white/70 dark:bg-slate-800/70 backdrop-blur-xl rounded-3xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden border-2 border-sky-200/50 dark:border-sky-700/50"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-gradient-to-r from-sky-600 to-blue-600 rounded-2xl mx-4 my-4 p-4 flex items-center justify-between sticky top-0 z-10">
            <h3 className="text-xl font-bold text-white drop-shadow-sm">{title}</h3>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-full p-2 transition-all duration-200 hover:scale-105"
              aria-label="Close"
            >
              <X size={24} />
            </button>
          </div>
          <div className="overflow-y-auto max-h-[calc(85vh-8rem)] px-6 pb-6 pt-4">
            {children}
          </div>
        </div>
      </div>
    );
  };

  const currentYear = new Date().getFullYear();

  return (
    <>
      <footer className="mt-20 py-8 border-t border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-4 text-xs sm:text-sm">
              <button
                onClick={() => {
                  openPrivacyPopup();
                  if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
                    window.gtag('event', 'privacy_modal_open', {
                      event_category: 'legal_documents',
                      event_label: 'Privacy Policy'
                    });
                  }
                }}
                className="text-slate-600 dark:text-slate-400 hover:text-sky-700 dark:hover:text-sky-300 transition-colors underline underline-offset-2"
              >
                {t('footer.privacy')}
              </button>
              <span className="text-slate-400 dark:text-slate-600">•</span>
              <button
                onClick={() => {
                  setShowCookiesModal(true);
                  if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
                    window.gtag('event', 'cookies_policy_open', {
                      event_category: 'legal_documents',
                      event_label: 'Cookie Policy'
                    });
                  }
                }}
                className="text-slate-600 dark:text-slate-400 hover:text-sky-700 dark:hover:text-sky-300 transition-colors underline underline-offset-2"
              >
                {t('footer.cookies')}
              </button>
              <span className="text-slate-400 dark:text-slate-600">•</span>
              <button
                onClick={() => {
                  openContactPopup();
                  if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
                    window.gtag('event', 'contact_open', {
                      event_category: 'navigation',
                      event_label: 'Contact'
                    });
                  }
                }}
                className="text-slate-600 dark:text-slate-400 hover:text-sky-700 dark:hover:text-sky-300 transition-colors underline underline-offset-2"
              >
                {t('footer.contact')}
              </button>
              {cookieConsent !== null && (
                <>
                  <span className="text-slate-400 dark:text-slate-600">•</span>
                  <button
                    onClick={() => {
                      setShowCookieBanner(true);
                      if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
                        window.gtag('event', 'cookie_preferences_open', {
                          event_category: 'user_preference',
                          event_label: 'Cookie Preferences Open'
                        });
                      }
                    }}
                    className="text-slate-600 dark:text-slate-400 hover:text-sky-700 dark:hover:text-sky-300 transition-colors underline underline-offset-2"
                  >
                    {t('footer.cookiePreferences')}
                  </button>
                </>
              )}
            </div>

            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
              <span>{t('footer.copyright', { year: currentYear })} {t('footer.madeWith')}</span>
              <Heart className="w-4 h-4 text-red-500 fill-current animate-pulse" />
              <span>{t('footer.forTravelers')}</span>
            </div>
          </div>
        </div>
      </footer>

      {/* Cookies Policy Modal */}
      <Modal
        isOpen={showCookiesModal}
        onClose={() => setShowCookiesModal(false)}
        title="Cookie Policy"
      >
        <div className="prose prose-sm sm:prose dark:prose-invert max-w-none">
          <p className="text-slate-600 dark:text-slate-400 mb-4">
            <strong>Last updated:</strong> December 31, 2025
          </p>

          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mt-6 mb-3">What Are Cookies?</h3>
          <p className="text-slate-700 dark:text-slate-300 mb-4">
            Cookies are small text files stored on your device by your web browser. They help websites function properly and provide information about usage.
          </p>

          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mt-6 mb-3">How We Use Cookies</h3>
          <p className="text-slate-700 dark:text-slate-300 mb-4">
            The TripReel site uses a minimal number of cookies, primarily for:
          </p>

          <div className="bg-sky-500/20 dark:bg-slate-700/50 rounded-lg p-4 mb-4">
            <h4 className="font-semibold text-slate-900 dark:text-white mb-2">🎨 Essential Preferences</h4>
            <p className="text-slate-700 dark:text-slate-300 text-sm mb-2">
              We store your theme preference (light/dark mode) in localStorage:
            </p>
            <ul className={`list-disc ${isRTL ? 'pr-6' : 'pl-6'} text-slate-700 dark:text-slate-300 text-sm space-y-1`}>
              <li><strong>Theme setting:</strong> Your visual preference for light or dark mode</li>
            </ul>
            <p className="text-slate-700 dark:text-slate-300 text-sm mt-2">
              <strong>Important:</strong> This preference is stored only on your device and contains no personal information.
            </p>
          </div>

          <div className="bg-purple-500/20 dark:bg-purple-900/20 rounded-lg p-4 mb-4">
            <h4 className="font-semibold text-slate-900 dark:text-white mb-2">📊 Google Analytics (Optional)</h4>
            <p className="text-slate-700 dark:text-slate-300 text-sm mb-2">
              With your consent, we use Google Analytics to understand website usage patterns:
            </p>
            <ul className={`list-disc ${isRTL ? 'pr-6' : 'pl-6'} text-slate-700 dark:text-slate-300 text-sm space-y-1`}>
              <li>Number of visitors</li>
              <li>Pages visited</li>
              <li>Time spent on site</li>
              <li>Traffic sources</li>
              <li>Device and browser types</li>
            </ul>
            <p className="text-slate-700 dark:text-slate-300 text-sm mt-2">
              <strong>Important:</strong> Google Analytics does not collect IP addresses, names, emails, or any data that could identify you personally.
            </p>
          </div>

          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mt-6 mb-3">What We Don't Do</h3>
          <div className="bg-green-500/20 dark:bg-green-900/20 rounded-lg p-4 mb-4">
            <ul className="list-none text-slate-700 dark:text-slate-300 text-sm space-y-2">
              <li>✅ No tracking cookies for advertising</li>
              <li>✅ No third-party advertising networks</li>
              <li>✅ No social media tracking pixels</li>
              <li>✅ No sale of data to third parties</li>
            </ul>
          </div>

          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mt-6 mb-3">Managing Cookies</h3>
          <p className="text-slate-700 dark:text-slate-300 mb-4">
            You can control and delete cookies through your browser settings. Most browsers allow you to block cookies or be notified when they are set.
          </p>

          <div className="bg-yellow-500/20 dark:bg-yellow-900/20 rounded-lg p-4 mb-4">
            <p className="text-slate-700 dark:text-slate-300 text-sm">
              <strong>Note:</strong> If you decline cookies, theme preferences cannot be saved and you'll see the system default theme. Analytics features will also be disabled.
            </p>
          </div>

          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mt-6 mb-3">Policy Updates</h3>
          <p className="text-slate-700 dark:text-slate-300 mb-4">
            We may update this policy occasionally. Changes will be posted on this page with the updated date.
          </p>

          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mt-6 mb-3">Questions?</h3>
          <p className="text-slate-700 dark:text-slate-300 mb-4">
            For questions about cookies: <button onClick={() => { setShowCookiesModal(false); openContactPopup(); }} className="text-sky-700 dark:text-sky-300 hover:underline">Contact page</button>
          </p>

          <div className="bg-gradient-to-r from-sky-500/20 to-blue-500/20 dark:from-sky-900/20 dark:to-blue-900/20 rounded-lg p-4 mt-6">
            <p className="text-sm text-slate-700 dark:text-slate-300 text-center">
              🔒 <strong>Your privacy matters to us.</strong> We use cookies responsibly and transparently to improve your experience.
            </p>
          </div>
        </div>
      </Modal>

      {/* Cookie Consent Banner */}
      {showCookieBanner && (
        <div className="fixed bottom-2 left-2 right-2 sm:bottom-3 sm:left-auto sm:right-3 sm:max-w-sm z-50">
          <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-3xl shadow-2xl border-2 border-sky-200/50 dark:border-sky-700/50 overflow-hidden">
            <div className={`bg-gradient-to-r from-sky-600 to-blue-600 rounded-2xl mx-3 mt-3 mb-1 py-1.5 flex items-center justify-between ${isRTL ? 'pr-4 pl-2' : 'pl-4 pr-2'}`}>
              <h3 className="text-sm font-bold text-white drop-shadow-sm">Cookies</h3>
              <button
                onClick={handleCloseBanner}
                className="text-white hover:bg-white/20 rounded-full p-1 transition-all duration-200 hover:scale-105"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-3">
              <p className="text-slate-800 dark:text-slate-300 text-xs mb-2 text-center">
                {t(`cookieBanner.${bannerMessage}.message`)}{' '}
                <button
                  onClick={() => setShowCookiesModal(true)}
                  className="text-sky-700 dark:text-sky-300 hover:underline font-medium"
                >
                  {t(`cookieBanner.${bannerMessage}.details`)}
                </button>
              </p>
              <div className="flex gap-1.5 justify-center">
                <button
                  onClick={handleAcceptCookies}
                  className="flex-1 bg-gradient-to-r from-sky-600 to-blue-600 hover:from-sky-700 hover:to-blue-700 text-white px-2 py-1.5 rounded-lg font-medium text-xs transition-all duration-200 shadow-md hover:shadow-lg hover:scale-105"
                >
                  {t(`cookieBanner.${bannerMessage}.accept`)}
                </button>
                <button
                  onClick={handleDeclineCookies}
                  className="flex-1 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-300 px-2 py-1.5 rounded-lg font-medium text-xs transition-all duration-200 hover:scale-105"
                >
                  {t(`cookieBanner.${bannerMessage}.decline`)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}